import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useConfig } from '@/context/ConfigContext';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Shield, Info, HelpCircle } from 'lucide-react';

const Login = () => {
  usePageTitle('Iniciar Sesión');
  const navigate = useNavigate();
  const { login } = useAuth();
  const config = useConfig();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(username, password);
      toast.success('¡Bienvenido!');
      
      // Redirect based on role
      switch (user?.role) {
        case 'scout':
          navigate('/players');
          break;
        case 'coach':
          navigate('/calendar');
          break;
        case 'intendente':
          navigate('/investments');
          break;
        case 'admin':
          navigate('/investments');
          break;
        case 'superadmin':
        case 'gerencia':
        case 'user':
        default:
          navigate('/');
          break;
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-700 p-4 relative">
      {/* Quick access icons */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <Link 
          to="/about" 
          className="p-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all duration-300 group"
          title="Acerca de"
        >
          <Info className="w-5 h-5 text-gray-400 group-hover:text-brand-400 transition-colors" />
        </Link>
        <Link 
          to="/manual" 
          className="p-2.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all duration-300 group"
          title="Manual de Uso"
        >
          <HelpCircle className="w-5 h-5 text-gray-400 group-hover:text-brand-400 transition-colors" />
        </Link>
      </div>

      <div className="w-full max-w-md fade-in">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img 
              src={config.AGENCY_LOGO_LIGHT_URL} 
              alt={config.AGENCY_NAME} 
              className="h-12" 
            />
          </div>
          <p className="text-gray-300">Sistema de Gestión para Representantes</p>
        </div>

        <Card className="glass-effect border-gray-700">
          <CardHeader>
            <CardTitle className="text-2xl text-center">Iniciar Sesión</CardTitle>
            <CardDescription className="text-center">
              Ingresa tus credenciales para acceder
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Usuario</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Ingresa tu usuario"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="h-11"
                  data-testid="login-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Ingresa tu contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11"
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-brand-600 hover:bg-brand-700 text-white font-medium"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;